import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { ArrowRight, Loader2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Contact = () => {
  const [form, setForm] = useState({ name: "", email: "", organization: "", message: "" });
  const [sending, setSending] = useState(false);

  const update = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    setSending(true);
    try {
      await axios.post(`${API}/contact`, form);
      toast.success("Message received. We'll be in touch soon.");
      setForm({ name: "", email: "", organization: "", message: "" });
    } catch {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section id="contact" data-testid="contact-section" className="relative px-6 sm:px-12 py-32 sm:py-44">
      <div className="grid lg:grid-cols-2 gap-12 lg:gap-20 glass-panel p-8 sm:p-14">
        <div>
          <div className="flex items-center gap-4 mb-8">
            <span className="h-px w-16 bg-clay" />
            <span className="text-xs uppercase tracking-[0.3em] font-bold text-gold">Contact</span>
          </div>
          <motion.h2
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.9, ease: [0.65, 0, 0.35, 1] }}
            className="font-display font-bold text-4xl sm:text-6xl tracking-tighter leading-[1.05]"
          >
            Let's build Africa's identity layer — <span className="font-serif-editorial italic font-medium text-clay">together</span>.
          </motion.h2>
          <p className="mt-10 max-w-md text-base md:text-lg text-ink-dim leading-relaxed">
            Governments, developers, researchers, and partners — if you believe in sovereign digital
            public infrastructure for Africa, we want to hear from you.
          </p>
        </div>

        <motion.form
          data-testid="contact-form"
          onSubmit={submit}
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-100px" }}
          transition={{ duration: 0.9, delay: 0.15, ease: [0.65, 0, 0.35, 1] }}
          className="flex flex-col gap-8 lg:pt-24"
        >
          <input data-testid="contact-name-input" className="input-line" placeholder="Your name" value={form.name} onChange={update("name")} required />
          <input data-testid="contact-email-input" className="input-line" type="email" placeholder="Email address" value={form.email} onChange={update("email")} required />
          <input data-testid="contact-org-input" className="input-line" placeholder="Organization (optional)" value={form.organization} onChange={update("organization")} />
          <textarea data-testid="contact-message-input" className="input-line resize-none" rows={4} placeholder="Your message" value={form.message} onChange={update("message")} required />
          <button data-testid="contact-submit-btn" type="submit" disabled={sending} className="btn-pill btn-pill-solid self-start disabled:opacity-60 group">
            {sending ? <Loader2 size={16} className="animate-spin" /> : null}
            {sending ? "Sending..." : "Send message"}
            {!sending && <ArrowRight size={16} className="transition-transform duration-300 group-hover:translate-x-1" />}
          </button>
        </motion.form>
      </div>
    </section>
  );
};

export default Contact;
